// #region Imports
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { FeeScheduleLoadIssue } from "marketplace/teams/fee-schedules/_stores/fee-schedule-redux-slice";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { useGetFeeScheduleTemplatesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import {
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import {
  getFeeScheduleTemplateQueryByInterface,
  getFeeScheduleTemplatesByQuery,
  isFeeScheduleTemplateLoadIssue,
  updateFeeScheduleTemplateInterfaceQuery
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import Pagination from "@mui/material/Pagination";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
// #endregion

// #region Styled Components
const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50),
}));
const PaginationControls = styled(Pagination)(({ theme }) => ({
  '&.MuiPagination-root > ul': {
    justifyContent: 'space-evenly'
  },
  marginBottom: theme.spacing(4),
  width: '100%'
}));
// #endregion

// #region Props/Types
type FeeScheduleExistingTemplateProps = {
  onTemplateSelected: (template: FeeScheduleTemplate) => void;
};

export default function FeeScheduleExistingTemplate(props: Readonly<FeeScheduleExistingTemplateProps>) {
  const { onTemplateSelected } = props;

  const pageSize = 25;
  const debounceDelay = 300;

  // #region State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const interfaceName = 'feeScheduleTemplateCategories';
  const feeScheduleTemplatesQueryParams = useSelector(getFeeScheduleTemplateQueryByInterface(interfaceName));

  const paginatedFeeScheduleTemplatesJson = useSelector(getFeeScheduleTemplatesByQuery(feeScheduleTemplatesQueryParams));
  const feeScheduleTemplates = useMemo(() => {
    if (!paginatedFeeScheduleTemplatesJson) return;
    const deserializedData: ((FeeScheduleTemplate | FeeScheduleLoadIssue) & { id: Guid })[] = [];
    for (const feeScheduleTemplateJson of paginatedFeeScheduleTemplatesJson.data) {
      try {
        if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
          deserializedData.push(feeScheduleTemplateJson);
        } else {
          const feeScheduleTemplate = FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
          if (feeScheduleTemplate?.id) {
            deserializedData.push(feeScheduleTemplate as FeeScheduleTemplate & { id: Guid });
          } else {
            console.error("FeeScheduleTemplate does not have an id", feeScheduleTemplateJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading FeeScheduleTemplate." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in feeScheduleTemplateJson
          ? (Guid.fromJSON(feeScheduleTemplateJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading FeeScheduleTemplate." });
        console.error("Error deserializing FeeScheduleTemplate JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedFeeScheduleTemplatesJson]);

  const feeScheduleTemplatesAPIQuery = useGetFeeScheduleTemplatesQuery(
    feeScheduleTemplatesQueryParams ? { query: feeScheduleTemplatesQueryParams, interfaceName } : skipToken,
  );

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceDelay);

    return () => globalThis.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (!session.user?.legalEntityId) {
      console.error("User does not have a legal entity ID");
      return;
    }

    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: session.user?.legalEntityId,
      query: debouncedSearchTerm,
    });
    const orderParameters = new FeeScheduleTemplateOrderParameters({
      orderBy: FeeScheduleTemplateOrderField.CreatedDate,
      orderDir: OrderDirection.Descending
    });

    const newQuery = new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters: new PaginationParameters({
        pageIndex: page - 1,
        pageSize,
      })
    });
    if (feeScheduleTemplatesQueryParams && newQuery.isEqualTo(feeScheduleTemplatesQueryParams)) return;
    dispatch(
      updateFeeScheduleTemplateInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    session.user?.legalEntityId.value,
    session,
    feeScheduleTemplatesQueryParams,
    dispatch,
    interfaceName,
    page,
    debouncedSearchTerm
  ]);

  useEffect(() => {
    if (feeScheduleTemplatesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule templates table", { variant: "error" });
    }
  }, [feeScheduleTemplatesAPIQuery.isError]);

  // #region Render
  if (feeScheduleTemplatesAPIQuery.isLoading) return <Loader />;

  return (
    <div>
      <TextField
        variant="outlined"
        fullWidth
        placeholder="Search Templates"
        disabled={!feeScheduleTemplates?.length && !searchTerm.length}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        slotProps={{
          input: {
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            endAdornment: searchTerm.length > 0 && (
              <Tooltip title="Clear search">
                <IconButton onClick={() => setSearchTerm("")} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ),
          }
        }}
      />
      <ListContainer>
        {feeScheduleTemplates?.length === 0 ? (
          <ListItem disableGutters>
            <ListItemText primary="No existing fee schedule templates" />
          </ListItem>
        ) : (
          feeScheduleTemplates?.map((template: FeeScheduleTemplate | FeeScheduleLoadIssue) => {
            if (isFeeScheduleTemplateLoadIssue(template)) {
              return (
                <ListItem key={template.id.value}>
                  <ListItemText
                    primary={`Error loading template (ID: ${template.id.value})`}
                    secondary={template.issue}
                  />
                </ListItem>
              );
            }

            return (
              <ListItemButton
                key={template.id?.value}
                onClick={() => onTemplateSelected(template)}
              >
                <ListItemAvatar><Avatar>E</Avatar></ListItemAvatar>
                <ListItemText
                  primary={template.name}
                  secondary={template.tags.map((tag) => (
                    <Chip key={tag.value} label={tag.value} size="small" />
                  ))}
                />
              </ListItemButton>
            );
          })
        )}
      </ListContainer>
      <PaginationControls
        count={Math.ceil((feeScheduleTemplates?.length ?? 0) / pageSize)}
        page={page}
        disabled={(feeScheduleTemplates?.length ?? 0) < 25}
        onChange={(_event, value) => setPage(value)}
      />
    </div>
  );
}
